import { useState, useEffect, useCallback, useRef } from 'react';
import { versionService, Version } from '../services/versionService';

export function useInfiniteVersions() {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const offsetRef = useRef(0);
  const mountedRef = useRef(false);

  const LOAD_LIMIT = 20;

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) {
      return;
    }

    const currentOffset = offsetRef.current;
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const newVersions = await versionService.getVersions(currentOffset, LOAD_LIMIT);

      if (newVersions.length === 0) {
        hasMoreRef.current = false;
        setHasMore(false);
      } else {
        setVersions(prev => [...prev, ...newVersions]);

        const newOffset = currentOffset + newVersions.length;
        offsetRef.current = newOffset;

        if (newVersions.length < LOAD_LIMIT) {
          hasMoreRef.current = false;
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error('❌ Error loading versions:', err);
      setError('Error al cargar versiones. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  const reset = useCallback(() => {
    setVersions([]);
    setHasMore(true);
    setError(null);
    setLoading(false);
    loadingRef.current = false;
    hasMoreRef.current = true;
    offsetRef.current = 0;
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    versions,
    loading,
    hasMore,
    error,
    loadMore,
    reset
  };
}
